// SPEC-054 / SPEC-072 / SPEC-092 — server-safe contract for the employees screen
// and the screen-permission model. No "use client" here: server pages
// (settings/employees/page.tsx) and client components (employees-client,
// reports-hub, AppSidebar) all import from this module, so it must not pull in
// any runtime client value.
//
// Backend contract:
//   GET    /api/telegram/employees  → {employees: EmployeeRow[], types: MessageType[]}
//   POST   /api/auth/users          body {phone,password,operation_codes[],...} → 201
//   PATCH  /api/auth/users/{id}     body {name?,position?,operation_codes?,...}  → 200
//   PUT    /api/telegram/settings   body {user_id,code,is_active}                → 204
//   GET    /api/auth/store-logins   → StoreLoginRow[]   (SPEC-092 M6)
//   GET    /api/auth/me             → carries operations: string[] | null
//
// `operations === null` ⇒ partner/admin (sees everything). A string[] ⇒
// partner_staff restricted to exactly those operation codes.
//
// SPEC-AUTH-149: access is granted by «section → tabs». `SECTION_CATALOG` below
// is the single source for sidebar visibility, section headers and the first
// screen; the backend mirror is `backoffice/src/backoffice/auth/operations.py`.

import { z } from "zod";

// ── Каталог прав-экранов (SPEC-092 REQ-OP-001) ─────────────────────────────
// Право = ЭКРАН, а не пункт меню: там, где два «экрана» делят одну ручку и
// разделить их гейтом нельзя, прав два не заводим (автозаказ/по-стилям —
// одна ручка с query forecast_level; списания — клон сравнительного).
//
// SPEC-AUTH-149 D14: the eight old action codes are dormant — they open nothing
// and live only in DORMANT_OPERATION_HEIRS below; buttons and tabs read tab codes.

export const OPERATION_DASHBOARD = "report.dashboard.view";
export const OPERATION_PURCHASE = "report.purchase.view";
export const OPERATION_DELIVERIES = "purchase.deliveries.view";
// SPEC-112 — вкладка «Закупка → Поставщики»: заведение поставщика, связка с
// юрлицом и разбор договора. Своё право-экран, а не финансовое: закупщику эта
// работа нужна без доступа к платежам (REQ-SEC-2).
export const OPERATION_SUPPLIERS_VIEW = "purchase.suppliers.view";
// SPEC-ASSORTMENTMATRIX-115 — вкладка «Матрица»: какие позиции магазин обязан
// держать на полке. SPEC-AUTH-149 D3: код вкладки даёт и правку.
export const OPERATION_MATRIX_VIEW = "purchase.matrix.view";
export const OPERATION_KEG = "purchase.keg.view";
export const OPERATION_KEG_TYPES = "purchase.keg.types.manage";
export const OPERATION_DELIVERY_RECON = "store.delivery_recon.view";
// SPEC-096: вкладка «Выручка» — своё право-экран (решение владельца
// 2026-07-18: вкладка принадлежит разделу «Упр. магазинами», не дашбордам).
export const OPERATION_STORE_REVENUE = "store.revenue.view";
export const OPERATION_SALES = "report.sales.view";
export const OPERATION_INVENTORY = "report.inventory.manage";
export const OPERATION_FIN_PAYMENTS = "finance.payments.view";
export const OPERATION_FIN_CALENDAR = "finance.calendar.view";
export const OPERATION_FIN_RECURRING = "finance.recurring.view";
export const OPERATION_FIN_PNL = "finance.pnl.view";
export const OPERATION_FIN_RECON = "finance.recon.view";
export const OPERATION_FIN_CASHDESKS = "finance.cashdesks.view";
// «Счета» — settlement accounts; own right (owner 27.09.2026), not granted with Кассы.
export const OPERATION_FIN_BANKACCOUNTS = "finance.bankaccounts.view";
export const OPERATION_FIN_SETTINGS = "finance.settings.view";
// SPEC-113 — «Розлив общепита»: продажи разливного из iiko и списание их в
// GainUp. Своё право, а не финблок [решение владельца 2026-08-17].
export const OPERATION_ROZLIV = "finance.rozliv.view";
export const OPERATION_TRANSFER_CREATE = "finance.transfer.create";
export const OPERATION_PAYMENT_CREATE = "finance.payment.create";
export const OPERATION_EMPLOYEES = "settings.employees.manage";
// SPEC-111 — «Зарплата». SPEC-AUTH-149 (design.md §2.15): четыре вкладки со
// своими кодами и четыре строки-деньги (ставки, ведение графика, закрытие
// журнала, выплаты). Правка графика по решению владельца выдаётся ТОЛЬКО учётке
// опердира. Правка журнала отделена от правки табеля 09.09.2026 (миграция 0105).
export const OPERATION_PAYROLL_RATES = "payroll.rates.view";
export const OPERATION_PAYROLL_STAFF = "payroll.staff.manage";
export const OPERATION_PAYROLL_TIMESHEET = "payroll.timesheet.edit";
export const OPERATION_PAYROLL_STATEMENT = "payroll.statement.edit";
export const OPERATION_PAYROLL_SCHEDULE = "payroll.schedule.edit";
export const OPERATION_PAYROLL_CLOSE = "payroll.statement.close";
export const OPERATION_PAYROLL_PAY = "payroll.pay";
// SPEC-098 — «База знаний». SPEC-AUTH-149 D3: код вкладки даёт и правку.
export const OPERATION_KB_VIEW = "kb.view";

// Раздел «Магазин» — рабочее место точки (SPEC-087/090/091). ОДНА галочка: доступ
// есть или нет, внутри — всё то же, что у владельца (приёмка, тара, касса, смены).
// Дробить нельзя: store.*-права гейтят ДЕЙСТВИЯ, а не вход в раздел, поэтому при
// частичной выдаче человек попадал бы на экран с неработающими кнопками.
// [HARD] store.cross_store в наборе обязателен: без него guard запирает человека в
// его ДОМАШНЮЮ точку, а у офисного сотрудника её нет → раздел пуст (403).
export const OPERATION_STORE = "store.cross_store";

/** Что реально выдаётся вместе с галочкой «Магазин» (весь набор рабочего места). */
export const STORE_SECTION_CODES: readonly string[] = [
  "store.cross_store",
  "store.receive",
  "store.keg.inventory",
  "store.attendance.mark",
  "store.cash.spend",
  "store.cash.collect",
];

/** Есть ли у сотрудника право `code` (владелец — всегда да). */
export function hasOperation(
  operations: readonly string[] | null,
  code: string,
): boolean {
  return operations === null || operations.includes(code);
}

/** Есть ли хоть одно право из набора (владелец — всегда да). */
export function hasAnyOperation(
  operations: readonly string[] | null,
  codes: readonly string[],
): boolean {
  if (operations === null) return true;
  return codes.some((code) => operations.includes(code));
}

/** Store-набор ЛК магазина — назначает сервер (SPEC-092 REQ-STORE-001а). */
export const STORE_LOGIN_CODES: readonly string[] = [
  "store.receive",
  "store.keg.inventory",
  "store.attendance.mark",
  "store.cash.spend",
  "store.cash.collect",
];

/** Разделы, открытые ВСЕМ без единого нашего права.
 *
 *  «НольПять» (SPEC-101, решение владельца 2026-07-28): собственного замка у
 *  раздела нет и не нужно — внутри ничего не видно, пока человек не вошёл в
 *  чужой сервис по своему телефону и коду из СМС. Право поверх этого лишь
 *  мешало раздавать доступ прилавку. Пункт меню видят все роли, включая ЛК
 *  магазина. */
export const OPEN_SECTIONS: readonly string[] = ["/nolpyat"];

/** Sections outside the catalog that only the owner sees (D17, R7): «Финансовая
 *  стратегия», «Чеки» (SPEC-POS-163). No employee code opens them. («Приход» and «ЕГАИС» left this list on
 *  22.10.2026, «Каталог», «Цены» and «Склад» on 27.10.2026 — catalog tabs now.) */
export const OWNER_ONLY_SECTIONS: readonly string[] = ["/strategy", "/checks"];

/** Sections outside the catalog for the owner and the store login (its operations
 *  carry store-login codes): «Фронт», the cash desk (SPEC-POS-156 REQ-POS-301). No
 *  operation code opens it; the page's server also asks /api/pos/front-access. */
export const STORE_LOGIN_SECTIONS: readonly string[] = ["/front"];

// ── Каталог «раздел → вкладки» (SPEC-AUTH-149 design.md §6) ─────────────────
// One tab — one right (D3): the tab's code opens everything on it. A shared
// screen (D11) is one code standing as a tab in several sections. Payroll money
// rows (D3) look like tabs in the drawer but open an action, not a screen; the
// host tab is the screen the action lives on.

export interface CatalogTab {
  /** Tab id — the vocabulary of the M0 fixtures (`orders.settlements`). */
  id: string;
  /** The word on the screen. */
  label: string;
  /** Where the tab lives (page, `?tab=` of the page, or the money row's host). */
  href: string;
  /** The tab's single right. */
  code: string;
  /** Codes the drawer checkbox grants when wider than `code` («Магазин» set). */
  grants?: readonly string[];
  /** Payroll money row: an action, not a screen (D3). */
  moneyRow?: true;
  /** Money row → tab id of the screen it acts on (none for «Ставки и суммы»). */
  hostTabId?: string;
}

export interface CatalogSection {
  /** Sidebar section key (`sectionKey ?? href` of the sidebar item). */
  key: string;
  /** Title in the drawer. */
  label: string;
  /** Short name for the «Разделы» column (mockup qcP1L). */
  short: string;
  /** One screen, no tabs to expand. */
  singleScreen: boolean;
  tabs: readonly CatalogTab[];
}

const section = (
  key: string,
  label: string,
  short: string,
  tabs: readonly CatalogTab[],
): CatalogSection => ({ key, label, short, singleScreen: tabs.length === 1, tabs });

// @MX:ANCHOR: [AUTO] single source of the section → tab rights (REQ-RIGHTS-001).
// @MX:REASON: sidebar, section headers, first screen, the employee drawer (M3)
// and the backend mirror (auth/operations.py CATALOG_OPERATION_CODES) all derive
// from it; the M7 guards compare it with the mirror and the section headers.
export const SECTION_CATALOG: readonly CatalogSection[] = [
  section("/retail", "Дашборды", "Дашборды", [
    { id: "retail.dashboard", label: "Дашборды", href: "/retail", code: OPERATION_DASHBOARD },
  ]),
  // One checkbox grants the whole point-of-sale set; owner-only (design.md §2.2).
  section("/store", "Магазин", "Магазин", [
    {
      id: "store.workplace",
      label: "Магазин",
      href: "/store",
      code: OPERATION_STORE,
      grants: STORE_SECTION_CODES,
    },
  ]),
  // «Каталог», «Цены» (SPEC-PRICES-152/153 — the right seeded by 0172) and «Склад»
  // (owner 27.10.2026): one tab — the whole section, reading and writing.
  section("/catalog", "Каталог", "Каталог", [
    { id: "catalog.manage", label: "Каталог", href: "/catalog", code: "catalog.manage" },
  ]),
  section("/prices", "Цены", "Цены", [
    { id: "prices.assign", label: "Цены", href: "/prices", code: "prices.assign" },
  ]),
  // 8 tabs as on production (D20).
  section("/orders", "Закупка", "Закупка", [
    { id: "orders.deliveries", label: "Поставки", href: "/orders/deliveries", code: OPERATION_DELIVERIES },
    { id: "orders.autoorder", label: "Автозаказ", href: "/orders", code: OPERATION_PURCHASE },
    { id: "orders.comparative", label: "Сравнительный отчёт", href: "/orders/comparative", code: OPERATION_SALES },
    { id: "orders.suppliers", label: "Поставщики", href: "/orders/suppliers", code: OPERATION_SUPPLIERS_VIEW },
    { id: "orders.settlements", label: "Расчёты", href: "/orders/settlements", code: "orders.settlements" },
    { id: "orders.requests", label: "Заявки магазинов", href: "/orders/requests", code: "orders.requests" },
    { id: "orders.schedule", label: "Расписание", href: "/orders/schedule", code: "orders.schedule" },
    { id: "orders.directory", label: "Справочник", href: "/orders/directory", code: "orders.directory" },
  ]),
  section("/edo", "ЭДО", "ЭДО", [
    { id: "edo.documents", label: "ЭДО", href: "/edo", code: "edo.documents" },
  ]),
  // Read rights (owner 22.10.2026): the code opens reading only — every write of
  // «Приход» (SPEC-RECEIPTS-148) and «ЕГАИС» (SPEC-EGAIS-150) stays the owner's.
  section("/receipts", "Приход", "Приход", [
    { id: "receipts.view", label: "Приход", href: "/receipts", code: "receipts.view" },
  ]),
  section("/egais", "ЕГАИС", "ЕГАИС", [
    { id: "egais.view", label: "ЕГАИС", href: "/egais", code: "egais.view" },
  ]),
  section("/stock", "Склад", "Склад", [
    { id: "stock.manage", label: "Склад", href: "/stock", code: "stock.manage" },
  ]),
  // «Паспорт» and «Планограммы» are off on production and get no rights (D21).
  section("/assortment", "Упр. ассортиментом", "Ассортимент", [
    { id: "assortment.matrix", label: "Матрица", href: "/assortment", code: OPERATION_MATRIX_VIEW },
    { id: "assortment.min", label: "Min", href: "/assortment/min", code: "assortment.min" },
    { id: "assortment.comments", label: "Комменты", href: "/assortment/comments", code: "assortment.comments" },
    { id: "assortment.history", label: "История изменений", href: "/assortment/history", code: "assortment.history" },
  ]),
  section("/aho", "АХО", "АХО", [
    { id: "aho.stock", label: "Остаток", href: "/aho/stock", code: "aho.stock" },
    { id: "aho.intake", label: "Приход", href: "/aho/intake", code: "aho.intake" },
    { id: "aho.issues", label: "Выдача", href: "/aho/issues", code: "aho.issues" },
    { id: "aho.directory", label: "Справочник", href: "/aho/directory", code: "aho.directory" },
  ]),
  // SPEC-POS-162 REQ-POS-1022: «Честный знак» — one right for the whole section (reading and
  // every action, «Подать» included); the signing switches stay the owner's (no screen).
  section("/chz", "Честный знак", "Честный знак", [
    { id: "chz.manage", label: "Честный знак", href: "/chz", code: "chz.manage" },
  ]),
  section("/stores/tare", "Оборотная тара", "Тара", [
    { id: "tare.overview", label: "Обзор", href: "/stores/tare", code: OPERATION_KEG },
    { id: "tare.journal", label: "Журнал", href: "/stores/tare?tab=journal", code: "tare.journal" },
    { id: "tare.acts", label: "Акты сверки", href: "/stores/tare?tab=acts", code: "tare.acts" },
  ]),
  section("/stores", "Упр. магазинами", "Магазины", [
    { id: "stores.delivery_recon", label: "Иные способы оплаты", href: "/stores/delivery-recon", code: OPERATION_DELIVERY_RECON },
    { id: "stores.revenue", label: "Выручка", href: "/stores/revenue", code: OPERATION_STORE_REVENUE },
    { id: "stores.comparative", label: "Сравнительный отчёт", href: "/stores/comparative", code: OPERATION_SALES },
    { id: "stores.writeoffs", label: "Списания и потери", href: "/stores/writeoffs", code: "reports.writeoffs" },
    { id: "stores.inventory", label: "Инвентаризации", href: "/stores/inventory", code: OPERATION_INVENTORY },
    // SPEC-POS-162 D8: «Магазины» — первый справочник раздела (адрес, ФИАС, GUID Меркурия).
    { id: "stores.settings.storages", label: "Справочники · Магазины", href: "/stores/settings", code: "stores.storages" },
    { id: "stores.settings.keg_types", label: "Справочники · Оборотная тара", href: "/stores/settings", code: OPERATION_KEG_TYPES },
    { id: "stores.settings.keg_suppliers", label: "Справочники · Поставщики", href: "/stores/settings", code: "stores.keg_suppliers" },
    { id: "stores.settings.plans", label: "Справочники · Планы продаж", href: "/stores/settings", code: "settings.plans.manage" },
  ]),
  section("/reports", "Отчёты", "Отчёты", [
    { id: "reports.comparative", label: "Сравнительный отчёт", href: "/reports/comparative", code: OPERATION_SALES },
    { id: "reports.smirnova", label: "Смирнова", href: "/reports/smirnova", code: OPERATION_SALES },
    { id: "reports.writeoffs", label: "Списания и потери", href: "/reports/writeoffs", code: "reports.writeoffs" },
    { id: "reports.inventory", label: "Инвентаризации", href: "/reports/inventory", code: OPERATION_INVENTORY },
  ]),
  section("/finance", "Финансы", "Финансы", [
    { id: "finance.payments", label: "Платежи", href: "/finance", code: OPERATION_FIN_PAYMENTS },
    { id: "finance.calendar", label: "Календарь", href: "/finance?tab=calendar", code: OPERATION_FIN_CALENDAR },
    { id: "finance.recon_acts", label: "Акты сверок", href: "/finance?tab=recon", code: OPERATION_FIN_RECON },
    { id: "finance.cashdesks", label: "Кассы", href: "/finance?tab=cashdesks", code: OPERATION_FIN_CASHDESKS },
    { id: "finance.bankaccounts", label: "Счета", href: "/finance?tab=accounts", code: OPERATION_FIN_BANKACCOUNTS },
    { id: "finance.debts", label: "Долги контрагентов", href: "/finance?tab=debts", code: "finance.debts" },
    { id: "finance.budget_articles", label: "Статьи бюджета", href: "/finance?tab=articles", code: "finance.budget_articles" },
    { id: "finance.directory", label: "Справочник", href: "/finance?tab=directory", code: OPERATION_FIN_SETTINGS },
  ]),
  section("/finance/rozliv", "Розлив общепита", "Розлив", [
    { id: "rozliv.writeoff", label: "Списание", href: "/finance/rozliv", code: OPERATION_ROZLIV },
    { id: "rozliv.cost", label: "Себестоимость", href: "/finance/rozliv?tab=cost", code: "rozliv.cost" },
  ]),
  // The same two rights open «Добавить платёж» / «Перевод» on «Платежи» (D13).
  section("/operations", "Операции", "Операции", [
    { id: "operations.transfer", label: "Перевод между счетами", href: "/operations", code: OPERATION_TRANSFER_CREATE },
    { id: "operations.payment", label: "Добавить платёж", href: "/operations", code: OPERATION_PAYMENT_CREATE },
  ]),
  section("/finance/expenses", "Расходы", "Расходы", [
    { id: "expenses.expenses", label: "Расходы", href: "/finance/expenses", code: OPERATION_FIN_RECURRING },
    { id: "expenses.deferred", label: "Расходы будущих периодов", href: "/finance/expenses?view=deferred", code: "expenses.deferred" },
  ]),
  // 4 tabs and 4 money rows (design.md §2.15). «Сотрудники» includes the
  // ⚙ Должности / Уровни продавца directories.
  section("/payroll", "Зарплата", "Зарплата", [
    { id: "payroll.staff", label: "Сотрудники", href: "/payroll", code: OPERATION_PAYROLL_STAFF },
    { id: "payroll.schedule", label: "График", href: "/payroll?tab=schedule", code: "payroll.schedule.view" },
    { id: "payroll.timesheet", label: "Табель", href: "/payroll?tab=timesheet", code: OPERATION_PAYROLL_TIMESHEET },
    { id: "payroll.statement", label: "Журнал", href: "/payroll?tab=statement", code: OPERATION_PAYROLL_STATEMENT },
    { id: "payroll.pay", label: "Выплаты", href: "/payroll?tab=statement", code: OPERATION_PAYROLL_PAY, moneyRow: true, hostTabId: "payroll.statement" },
    { id: "payroll.close", label: "Закрытие журнала", href: "/payroll?tab=statement", code: OPERATION_PAYROLL_CLOSE, moneyRow: true, hostTabId: "payroll.statement" },
    { id: "payroll.rates", label: "Ставки и суммы", href: "/payroll", code: OPERATION_PAYROLL_RATES, moneyRow: true },
    { id: "payroll.schedule_edit", label: "Ведение графика", href: "/payroll?tab=schedule", code: OPERATION_PAYROLL_SCHEDULE, moneyRow: true, hostTabId: "payroll.schedule" },
  ]),
  section("/accounting", "Учёт", "Учёт", [
    { id: "accounting.pnl", label: "ОПУ", href: "/accounting/pnl", code: OPERATION_FIN_PNL },
    { id: "accounting.cashflow", label: "ДДС", href: "/accounting/cashflow", code: "accounting.cashflow" },
    { id: "accounting.balance", label: "Баланс", href: "/accounting/balance", code: "accounting.balance" },
    { id: "accounting.suppliers", label: "Поставщики", href: "/accounting/suppliers", code: "accounting.suppliers" },
    { id: "accounting.stock", label: "Товар по складам", href: "/accounting/stock", code: "accounting.stock" },
    { id: "accounting.closing", label: "Закрытие месяца", href: "/accounting/closing", code: "accounting.closing" },
    { id: "accounting.budget_articles", label: "Статьи бюджета", href: "/accounting/articles", code: "finance.budget_articles" },
    { id: "accounting.fixed_assets", label: "Основные средства", href: "/accounting/fixed-assets", code: "accounting.fixed_assets" },
  ]),
  section("/taxes", "Налоги", "Налоги", [
    { id: "taxes.accruals", label: "Начисления", href: "/taxes/accruals", code: "taxes.accruals" },
    { id: "taxes.wallet", label: "Кошелёк ЕНС", href: "/taxes/wallet", code: "taxes.wallet" },
    { id: "taxes.settings", label: "Справочник", href: "/taxes/settings", code: "taxes.settings" },
  ]),
  section("/kb", "База знаний", "База знаний", [
    { id: "kb.kb", label: "База знаний", href: "/kb", code: OPERATION_KB_VIEW },
  ]),
  // «ЛК магазинов» — an ordinary «Настройки» tab (D25).
  section("/settings", "Настройки", "Настройки", [
    { id: "settings.employees", label: "Сотрудники", href: "/settings/employees", code: OPERATION_EMPLOYEES },
    { id: "settings.store_logins", label: "ЛК магазинов", href: "/settings/stores", code: "settings.store_logins" },
  ]),
];

/** Codes a tab's checkbox grants (the «Магазин» set, otherwise its own code). */
export function tabCodes(tab: CatalogTab): readonly string[] {
  return tab.grants ?? [tab.code];
}

/** Every code of the catalog — equals the backend `CATALOG_OPERATION_CODES`. */
export const CATALOG_OPERATION_CODES: ReadonlySet<string> = new Set(
  SECTION_CATALOG.flatMap((s) => s.tabs.flatMap(tabCodes)),
);

/** Rows only the owner grants: the «Магазин» set and the payroll money rows. */
export const OWNER_ONLY_OPERATION_CODES: ReadonlySet<string> = new Set(
  SECTION_CATALOG.flatMap((s) =>
    s.tabs.filter((t) => t.grants !== undefined || t.moneyRow).flatMap(tabCodes),
  ),
);

/** Dormant code → heir tab codes (design.md §3, D14) — the single list of the
 *  eight dormant codes. A dormant code opens nothing; unticking any heir tab
 *  drops it (drawer M3, backend `auth/operations.py` DORMANT_OPERATION_HEIRS). */
export const DORMANT_OPERATION_HEIRS: Readonly<Record<string, readonly string[]>> = {
  "purchase.suppliers.manage": [OPERATION_SUPPLIERS_VIEW],
  "purchase.matrix.manage": [OPERATION_MATRIX_VIEW],
  "purchase.keg.recount": ["tare.journal"],
  "purchase.keg.adjust": ["tare.journal"],
  "purchase.keg.opening": ["tare.journal"],
  "purchase.deliveries.accept": ["tare.journal"],
  "kb.edit": [OPERATION_KB_VIEW],
  "payroll.view": [
    OPERATION_PAYROLL_STAFF,
    "payroll.schedule.view",
    OPERATION_PAYROLL_TIMESHEET,
    OPERATION_PAYROLL_STATEMENT,
  ],
};

/** Screen tabs of `section` the person sees (money rows are not screens). */
export function visibleTabs(
  catalogSection: CatalogSection,
  operations: readonly string[] | null,
): CatalogTab[] {
  return catalogSection.tabs.filter(
    (t) => !t.moneyRow && hasAnyOperation(operations, tabCodes(t)),
  );
}

const SECTION_BY_KEY = new Map(SECTION_CATALOG.map((s) => [s.key, s]));
const TAB_BY_ID = new Map(SECTION_CATALOG.flatMap((s) => s.tabs.map((t) => [t.id, t] as const)));

/**
 * Holds the person the screen tab `tabId` of the catalog? The tab's code opens
 * everything on it — reading, editing, actions (D3) — so section headers and
 * edit buttons both ask this. Owner/admin (`null`) — always; a money row or an
 * id outside the catalog — never.
 */
// @MX:ANCHOR: [AUTO] the one tab check of headers and edit buttons (REQ-RIGHTS-003, 013).
// @MX:REASON: every section header, the sidebar landing and the edit buttons of
// kb / matrix / planogram / Min / comments / tare call it; M7 guards compare the
// header tab ids with the catalog.
export function canSeeTab(tabId: string, operations: readonly string[] | null): boolean {
  if (operations === null) return true;
  const tab = TAB_BY_ID.get(tabId);
  return tab !== undefined && !tab.moneyRow && hasAnyOperation(operations, tabCodes(tab));
}

/** Where the sidebar item of section `key` leads: the first tab the person
 *  holds (REQ-RIGHTS-012); `fallback` when none or for the owner. */
export function sectionItemHref(
  key: string,
  operations: readonly string[] | null,
  fallback: string,
): string {
  if (operations === null) return fallback;
  return sectionLandingHref(key, operations, fallback);
}

/**
 * Виден ли сотруднику раздел за `href` (ключ пункта сайдбара)?
 * `operations === null` (владелец/админ) ⇒ всегда да. Раздел каталога виден,
 * если видна хоть одна его вкладка; раздел вне каталога сотруднику скрыт.
 */
export function canSeeSection(
  href: string,
  operations: readonly string[] | null,
): boolean {
  if (OPEN_SECTIONS.includes(href)) return true;
  if (operations === null) return true;
  if (OWNER_ONLY_SECTIONS.includes(href)) return false;
  if (STORE_LOGIN_SECTIONS.includes(href)) {
    return operations.some((code) => STORE_LOGIN_CODES.includes(code));
  }
  const catalogSection = SECTION_BY_KEY.get(href);
  return catalogSection !== undefined && visibleTabs(catalogSection, operations).length > 0;
}

/** First visible tab of the section `key`, or `fallback` when none is. */
function sectionLandingHref(
  key: string,
  operations: readonly string[] | null,
  fallback: string,
): string {
  const catalogSection = SECTION_BY_KEY.get(key);
  if (catalogSection === undefined) return fallback;
  return visibleTabs(catalogSection, operations)[0]?.href ?? fallback;
}

/** Первый доступный сотруднику экран — куда его вести с «/» и после входа. */
export function landingHref(operations: readonly string[] | null): string {
  if (operations === null) return "/retail";
  // ЛК магазина (SPEC-092 D16, единый вход): его набор — store.*; единственный
  // рабочий экран точки — /store (офисного каталога у него нет).
  if (operations.some((code) => STORE_LOGIN_CODES.includes(code))) return "/store";
  for (const catalogSection of SECTION_CATALOG) {
    const first = visibleTabs(catalogSection, operations)[0];
    if (first !== undefined) return first.href;
  }
  return "/app";
}

/** Куда ведёт пункт «Упр. магазинами»: первая открытая вкладка раздела (единый
 *  источник для сайдбара И redirect `/stores`). */
export function storesLandingHref(operations: readonly string[] | null): string {
  return sectionLandingHref("/stores", operations, "/stores/delivery-recon");
}

// ── Wire schemas (GET /api/telegram/employees envelope) ─────────────────────

export const messageTypeSchema = z.object({
  code: z.string(),
  title: z.string(),
});

export type MessageType = z.infer<typeof messageTypeSchema>;

export const tgSubscriptionSchema = z.object({
  code: z.string(),
  is_active: z.boolean(),
});

export const employeeRowSchema = z.object({
  id: z.number().int(),
  phone: z.string(),
  name: z.string().nullish(),
  position: z.string().nullish(),
  no_login: z.boolean(),
  bound: z.boolean(),
  is_active: z.boolean(),
  operations: z.array(z.string()),
  settings: z.array(tgSubscriptionSchema),
});

/** Строка сотрудника из конверта — и для списка, и для шторки. */
export type StaffListItem = z.infer<typeof employeeRowSchema>;

export const employeesEnvelopeSchema = z.object({
  employees: z.array(employeeRowSchema),
  types: z.array(messageTypeSchema),
});

export interface EmployeesEnvelope {
  employees: StaffListItem[];
  types: MessageType[];
}

/** Разобрать конверт сотрудников; при несовпадении схемы — пустой конверт,
 *  страница рисует пустое состояние вместо падения (fail-soft). */
export function parseEmployeesEnvelope(json: unknown): EmployeesEnvelope {
  const parsed = employeesEnvelopeSchema.safeParse(json);
  return parsed.success ? parsed.data : { employees: [], types: [] };
}

// ── ЛК магазинов (SPEC-092 M6) ─────────────────────────────────────────────

export const storeLoginRowSchema = z.object({
  storage_id: z.number().int(),
  storage_name: z.string(),
  user_id: z.number().int().nullish(),
  phone: z.string().nullish(),
  is_active: z.boolean().nullish(),
  has_login: z.boolean(),
});

export type StoreLoginRow = z.infer<typeof storeLoginRowSchema>;

export const storeLoginsSchema = z.array(storeLoginRowSchema);

/** Разобрать список ЛК магазинов; при несовпадении схемы — пустой список. */
export function parseStoreLogins(json: unknown): StoreLoginRow[] {
  const parsed = storeLoginsSchema.safeParse(json);
  return parsed.success ? parsed.data : [];
}
